import React, { useState, useEffect, Fragment } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { debounce } from 'lodash';
import {
  CRUDLayout,
  Alert,
  DataStatus,
  ActionButton,
  InputSearch,
  Pagination,
  Table,
  THead,
  TBody,
  Tr,
  ThFixed,
  TdFixed,
  Th,
  Td,
} from 'components';
import { PageNumber as TableNumber } from 'utilities';
import { Api } from '../apis';
import { dateConvert, rupiahConvert } from '@bhawanadevteam/react-core';

const HistoryApproval = () => {
  const history = useHistory();
  const location = useLocation();
  const date = dateConvert();
  const rupiah = rupiahConvert();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [data, setData] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    page: '1',
    dataLength: '10',
    totalPage: '1',
    dataCount: '0',
  });
  const [searchConfig, setSearchConfig] = useState({
    status: false,
    key: '',
  });
  const [alertConfig, setAlertConfig] = useState({
    show: false,
    variant: 'primary',
    text: '',
  });

  const getInitialData = () => {
    setIsPageLoading(true);

    Api.getHistory({
      q: searchConfig.key,
      page: paginationConfig.page,
      per_page: paginationConfig.dataLength,
    })
      .then((res) => {
        const data = res.data;
        setData(data ? data.data : []);
        setPaginationConfig({
          ...paginationConfig,
          dataCount: data.data_count,
          totalPage: data.total_page,
        });
        searchConfig.status &&
          setAlertConfig({
            show: true,
            variant: 'primary',
            text: `Hasil dari pencarian: ${searchConfig.key}`,
          });
      })
      .catch((err) => {
        setAlertConfig({
          show: true,
          variant: 'danger',
          text: 'Data gagal dimuat!',
        });
      })
      .finally(() => setIsPageLoading(false));
  };

  const checkAlert = () => {
    const locationState = location.state;

    if (locationState) {
      if (locationState.alert) {
        setAlertConfig({
          show: locationState.alert.show,
          text: locationState.alert.text,
          variant: locationState.alert.variant,
        });
      }
    }
  };

  const generateStatus = (status) => {
    if (status.toUpperCase() === 'REV') {
      return 'REVISI';
    }

    if (status.toUpperCase() === 'VER') {
      return 'VERIFIED';
    }

    if (status.toUpperCase() === 'APP') {
      return 'APPROVED';
    }

    return 'PENDING';
  };

  const searchHandler = (e) => {
    setSearchConfig({ ...searchConfig, key: e.target.value });
  };

  useEffect(() => {
    getInitialData();
    checkAlert();

    return () => {
      setIsPageLoading(false);
    };
  }, [searchConfig.key, paginationConfig.page, paginationConfig.dataLength]);

  const PageContent = () => {
    const DataTable = () => (
      <Table>
        <THead>
          <Tr>
            <ThFixed>No</ThFixed>
            <Th>Tgl. & No. Rencana Proyek</Th>
            <Th>Baseline</Th>
            <Th>Skup Proyek</Th>
            <Th>Item BQ</Th>
            <Th>Total Harga</Th>
            <Th>Total Hari</Th>
            <Th>Status Approval</Th>
            <Th>Detail Pengajuan</Th>
          </Tr>
        </THead>
        <TBody>
          {data.map((val, index) => (
            <Tr key={index}>
              <TdFixed>
                {TableNumber(
                  paginationConfig.page,
                  paginationConfig.dataLength,
                  index
                )}
              </TdFixed>
              <Td>
                <div>
                  {val?.tgl_rencana_proyek
                    ? date.getSlashDMY(new Date(val.tgl_rencana_proyek))
                    : '-'}
                </div>
                <div className="text-break">
                  {val?.no_rencana_proyek ?? '-'}
                </div>
              </Td>
              <Td>{val.baseline ?? '-'}</Td>
              <Td>{val.nama_skup_proyek ?? '-'}</Td>
              <Td>
                {val?.item_bq?.length === 0 ? (
                  <div>-</div>
                ) : (
                  val?.item_bq?.map((val, index) => (
                    <Fragment>
                      {index > 0 && <hr />}

                      <section>
                        <div key={index} className="text-break">
                          {val.no_spk}
                        </div>
                        <ul className="pl-3">
                          {val.item_spk?.map((val, index) => (
                            <li key={index}>{val.nama_barang_jadi}</li>
                          ))}
                        </ul>
                      </section>
                    </Fragment>
                  ))
                )}
              </Td>

              <TdFixed>
                {rupiah.getWithComa(String(val?.total_harga ?? 0))}
              </TdFixed>
              <TdFixed textRight className="text-nowrap">
                {val?.total_hari ?? '-'} Hari
              </TdFixed>
              <TdFixed textCenter>
                {val?.status_approval
                  ? generateStatus(val.status_approval)
                  : '-'}
              </TdFixed>
              <Td>
                <div className="d-flex justify-content-center">
                  <ActionButton
                    size="sm"
                    text="Lihat Detail"
                    className="col"
                    onClick={() =>
                      history.push(
                        '/human-resource/approval/rencana-proyek/detail' +
                          val.id_rencana_proyek,
                        {
                          tab: 'history',
                        }
                      )
                    }
                  />
                </div>
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    );

    return (
      <>
        <DataTable />
        {!searchConfig.status && (
          <Pagination
            dataLength={paginationConfig.dataLength}
            dataNumber={
              paginationConfig.page * paginationConfig.dataLength -
              paginationConfig.dataLength +
              1
            }
            dataPage={
              paginationConfig.dataCount < paginationConfig.dataLength
                ? paginationConfig.dataCount
                : paginationConfig.page * paginationConfig.dataLength
            }
            dataCount={paginationConfig.dataCount}
            currentPage={paginationConfig.page}
            totalPage={paginationConfig.totalPage}
            onPaginationChange={({ selected }) =>
              setPaginationConfig({
                ...paginationConfig,
                page: selected + 1,
              })
            }
            onDataLengthChange={(e) =>
              setPaginationConfig({
                ...paginationConfig,
                page: 1,
                dataLength: e.target.value,
              })
            }
          />
        )}
      </>
    );
  };

  return (
    <CRUDLayout>
      {/* head section */}
      <CRUDLayout.Head>
        <CRUDLayout.HeadSearchSection>
          <Row className="mb-2">
            <Col md={8}>
              <InputSearch
                onChange={debounce(searchHandler, 1500)}
                onSubmit={(e) => e.preventDefault()}
              />
            </Col>
          </Row>
        </CRUDLayout.HeadSearchSection>
        <CRUDLayout.HeadButtonSection></CRUDLayout.HeadButtonSection>
      </CRUDLayout.Head>

      {/* alert */}
      <Alert
        show={alertConfig.show}
        variant={alertConfig.variant}
        text={alertConfig.text}
        showCloseButton={true}
        onClose={() =>
          setAlertConfig({
            ...alertConfig,
            show: false,
          })
        }
      />

      {/* content */}
      {isPageLoading ? (
        <DataStatus loading={true} text="Memuat data . . ." />
      ) : data ? (
        data.length > 0 ? (
          <PageContent />
        ) : (
          <DataStatus text="Tidak ada data" />
        )
      ) : (
        <DataStatus text="Data gagal dimuat" />
      )}
    </CRUDLayout>
  );
};

export default HistoryApproval;
